/**
 * Builds the prompt and navigation target for the "Ask Ahoy what went wrong"
 * CTA on a failed automation run — the timeline sibling of
 * `AutomationRanEvent.tsx`.
 *
 * Both helpers are pure: they build a string and a nav target without
 * touching routing, the CRM store, or the network. The click handler in the
 * timeline event navigates with the result.
 */
import {
  buildEditInBuilderNavState,
  type BuilderModeNavState,
} from '@/lib/crm/components/badges/buildBuilderModeNavState';
import { buildChatAboutNavState } from '@/lib/crm/components/badges/buildChatAboutNavState';

type RunProvenance = 'clock' | 'manual' | 'event' | undefined;

const RUN_NOUNS: Record<'clock' | 'manual' | 'event', string> = {
  clock: 'scheduled run',
  manual: 'manual run',
  event: 'event-triggered run',
};

// Failure categories are internal slugs (apps/automations); only forward one
// that looks like a slug into the prompt rather than leaking arbitrary text.
const CATEGORY_PATTERN = /^[a-z0-9_]{1,64}$/;

export interface AutomationFailurePromptInput {
  provenance: RunProvenance;
  occurredAt: string;
  timezone: string;
  reason: string | null;
  category: string | undefined;
}

/**
 * Builds the prompt that seeds a new conversation investigating a failed
 * automation run.
 *
 * The prompt is built ONLY from fixed copy, the run's timestamp and
 * provenance, the fixed reason sentence already shown on the timeline event,
 * and a shape-checked `category` slug. It NEVER includes `payload.summary`:
 * that field is script-controlled log text headed for an agent that can
 * propose writes, so it must never be interpolated into an agent prompt.
 */
export function buildAutomationFailurePrompt(
  input: AutomationFailurePromptInput
): string {
  const { provenance, occurredAt, timezone, reason, category } = input;
  const runNoun = provenance ? RUN_NOUNS[provenance] : 'run';
  const date = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(new Date(occurredAt));

  let prompt = `This automation's ${runNoun} on ${date} failed.`;
  if (reason !== null) {
    prompt += ` Ahoy showed this message: ${reason}`;
  }
  if (category && CATEGORY_PATTERN.test(category)) {
    prompt += ` The error code was ${category}.`;
  }
  prompt += ' Can you look into what went wrong and suggest a fix?';
  return prompt;
}

export interface AutomationFailureChatNavInput {
  orgId: number;
  automationEntityId: string;
  automationEntityType: string;
  automationDisplayName: string;
  canEditInBuilder: boolean;
  prompt: string;
}

/**
 * Builds the navigation target for the "Ask Ahoy what went wrong" CTA.
 *
 * Every click starts a NEW conversation — the prompt is only prefilled into
 * the composer, never auto-sent.
 *
 * Users who can edit the automation in the builder land on a new builder
 * conversation grounded on it; everyone else lands on an ordinary "chat about
 * this automation" conversation, with the prompt appended after the record's
 * `<Entity>` mention.
 */
export function buildAutomationFailureChatNavState(
  input: AutomationFailureChatNavInput
): BuilderModeNavState {
  const {
    orgId,
    automationEntityId,
    automationEntityType,
    automationDisplayName,
    canEditInBuilder,
    prompt,
  } = input;

  if (canEditInBuilder) {
    return buildEditInBuilderNavState(
      orgId,
      automationEntityType,
      automationEntityId,
      prompt
    );
  }

  const { pathname, state } = buildChatAboutNavState({
    orgId,
    entityId: automationEntityId,
    entityType: automationEntityType,
    displayName: automationDisplayName,
  });
  return {
    to: pathname,
    state: {
      ...state,
      initialPrompt: `${state.initialPrompt}${prompt}`,
    },
  };
}
